import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { isBrowserAuthConnection } from 'vet-bones/utils';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import { Loading } from 'src/ui/components/Loading';
import { Welcome } from 'src/ui/containers/landing/Welcome';
import { ConnectionDetails } from 'src/ui/containers/launchpad/dashboard/ConnectionDetails';
import { NewUserLanding } from 'src/ui/containers/launchpad/dashboard/NewUserLanding';
import { StardogApps } from 'src/ui/containers/launchpad/dashboard/StardogApps';
import {
  Connection,
  connectionInvalid,
  deleteConnection,
  setConnectionBrowserAuthFailed,
  setConnectionSSOError,
  setConnectionUnauthorized,
} from 'src/ui/features/connection';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection as GraphConnection,
  useReauthenticateSsoConnectionMutation,
  useSavedConnectionsQuery,
} from 'src/ui/graph/types';
import {
  getSSOTokenErrorCode,
  isConnectionTimeoutError,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useOnError } from 'src/ui/hooks/graph';
import { useVersion } from 'src/ui/hooks/version';
import {
  isBrowserAuthFailureSelector,
  isUnauthorizedSelector,
} from 'src/ui/selectors/connection';
import * as copy from 'src/ui/templates/copy';
import {
  doDesignerRedirect,
  doExplorerRedirect,
  doStudioRedirect,
  redirectToUrl,
} from 'src/ui/utils/window';

type TParams = { id?: string };

export const LaunchpadDashboard: React.VFC = () => {
  const { data, isLoading, isFetching } = useSavedConnectionsQuery(
    graphQLClient,
    {}
  );
  const { data: versionData } = useVersion();

  const { mutate: verifyConnection } = useVerifyConnection();
  const [isValidated, setIsValidated] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();
  const isBrowserAuthFailure = useAppSelector(isBrowserAuthFailureSelector);
  const isUnauthorized = useAppSelector(isUnauthorizedSelector);
  const history = useHistory();
  const params = useParams<TParams>();
  const [
    currentConnection,
    setCurrentConnection,
  ] = React.useState<GraphConnection | null>(null);

  const onError = useOnError();

  // istanbul ignore next
  const {
    mutate: reauthenticateSSOConnectionMutation,
  } = useReauthenticateSsoConnectionMutation(graphQLClient, {
    onError,
    onSuccess: (data) => {
      if (data?.reauthenticateSSOConnection) {
        redirectToUrl(data?.reauthenticateSSOConnection?.redirect_url);
      }
    },
  });

  React.useEffect(() => {
    if (data?.listConnections) {
      if (params.id) {
        const connection =
          data.listConnections.find((c) => c?.index === Number(params.id)) ||
          null;
        if (connection) {
          setCurrentConnection(connection);
        } else if (!isFetching) {
          // Connection at params.id was deleted or not found, go home
          // Only redirect if we're not fetching (to handle newly created connections)
          setCurrentConnection(null);
          history.push('/');
        }
      } else if (data.listConnections.length > 0 && !currentConnection) {
        const firstConnection = data.listConnections[0];
        setCurrentConnection(firstConnection);
        if (history.location.pathname !== `/u/${firstConnection?.index}`) {
          history.push(`/u/${firstConnection?.index}`);
        }
      } else if (
        data.listConnections.length === 0 &&
        history.location.pathname !== '/'
      ) {
        history.push('/');
      }
    }
  }, [data, params, currentConnection, history, isFetching]);

  // istanbul ignore next
  React.useEffect(() => {
    if (!currentConnection || isValidated || isUnauthorized) {
      return;
    }
    verifyConnection(currentConnection as Connection, {
      onSuccess: () => setIsValidated(true),
      onError: (error) => {
        if (isConnectionTimeoutError(error)) {
          dispatch(setConnectionUnauthorized(true));
          return;
        }

        const ssoCode = getSSOTokenErrorCode(error);
        if (ssoCode) {
          dispatch(setConnectionSSOError(ssoCode));
          return;
        }

        if (currentConnection.useConnectionSSO) {
          // If they are using sso, then there they are most likely not
          // authorized or some other error with communication
          dispatch(setConnectionUnauthorized(true));
        } else if (isBrowserAuthConnection(currentConnection)) {
          // If useBrowserAuth was enabled and validate connection failed,
          // show banner warning for this
          dispatch(setConnectionBrowserAuthFailed(true));
        } else {
          // If they are using Stardog token it has expired and they need
          // re-enter their password to get a new token.
          dispatch(setConnectionUnauthorized(true));
          dispatch(connectionInvalid(currentConnection?.index));
          dispatch(openedDialog({ type: DialogType.SIGN_IN }));
        }
      },
    });
  }, [
    isValidated,
    isUnauthorized,
    currentConnection,
    verifyConnection,
    dispatch,
  ]);

  React.useEffect(() => {
    setIsValidated(false);
    dispatch(setConnectionBrowserAuthFailed(false));
    dispatch(setConnectionUnauthorized(false));
    dispatch(setConnectionSSOError(null));
  }, [currentConnection, dispatch]);

  // istanbul ignore next
  const handleReauthenticateSSOConnection = () => {
    if (currentConnection) {
      reauthenticateSSOConnectionMutation({
        input: { connection_id: currentConnection?.id },
      });
    }
  };

  // istanbul ignore next
  const handleReauthenticateUserPasswordConnection = () => {
    if (currentConnection) {
      dispatch(connectionInvalid(currentConnection?.index));
      dispatch(openedDialog({ type: DialogType.SIGN_IN }));
    }
  };

  // istanbul ignore next
  const handleDeleteConnection = () => {
    if (currentConnection) {
      dispatch(deleteConnection(currentConnection?.index));
      dispatch(openedDialog({ type: DialogType.DELETE_ENDPOINT }));
    }
  };

  const connections =
    data?.listConnections?.filter((c): c is GraphConnection => c !== null) ??
    [];

  if (isLoading) {
    return <Loading />;
  }

  if (connections.length === 0) {
    return <NewUserLanding />;
  }

  const subText = versionData?.version ? (
    <span className="version-text">v{versionData.version}</span>
  ) : (
    ''
  );

  return (
    <div>
      <Welcome
        titleText={copy.components.launchpad.dashboard.title}
        subText={subText}
        className="welcome-container-voicebox"
      />
      {currentConnection ? (
        <div className="sd-landing-dashboard">
          <ConnectionDetails
            connection={currentConnection}
            onReauthenticateSSOConnection={handleReauthenticateSSOConnection}
            onReauthenticateUserPasswordConnection={
              handleReauthenticateUserPasswordConnection
            }
            onDeleteConnection={handleDeleteConnection}
            isUnauthorized={isUnauthorized}
            isBrowserAuthFailure={isBrowserAuthFailure}
          />

          <StardogApps
            currentIndex={currentConnection.index}
            goToExplorer={(idx) => doExplorerRedirect('', idx)}
            goToStudio={(idx) => doStudioRedirect('', idx)}
            goToDesigner={(idx) => doDesignerRedirect('', idx)}
          />
        </div>
      ) : null}
    </div>
  );
};
